import { useState, useCallback } from 'react';

export type ValidationRule<T> = {
  field: keyof T;
  validate: (value: any) => string | null;
};

export type ValidationErrors = {
  [key: string]: string;
};

export const useFormValidation = <T>(validationRules: ValidationRule<T>[]) => {
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Clear individual field error
  const clearFieldError = useCallback((fieldName: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  // Validate single field and update errors state
  const validateFieldOnChange = useCallback((fieldName: keyof T, value: any) => {
    const rule = validationRules.find(rule => rule.field === fieldName);
    const error = rule ? rule.validate(value) : null;
    
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[String(fieldName)] = error;
      } else {
        delete newErrors[String(fieldName)];
      }
      return newErrors;
    });
    
    return error;
  }, [validationRules]);

  // Validate single field without updating state
  const validateField = useCallback((fieldName: keyof T, value: any): string | null => {
    const rule = validationRules.find(rule => rule.field === fieldName);
    return rule ? rule.validate(value) : null;
  }, [validationRules]);

  // Check if form is valid without triggering validation
  const isFormValid = useCallback((data: T): boolean => {
    return validationRules.every(rule => {
      const fieldValue = data[rule.field];
      const error = rule.validate(fieldValue);
      return !error;
    });
  }, [validationRules]);

  // Validate all fields and update errors state
  const validateAll = useCallback((data: T): { isValid: boolean; errors: ValidationErrors } => {
    const newErrors: ValidationErrors = {};

    validationRules.forEach(rule => {
      const fieldValue = data[rule.field];
      const error = rule.validate(fieldValue);
      if (error) {
        newErrors[String(rule.field)] = error;
      }
    });

    setErrors(newErrors);
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  }, [validationRules]);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    validateField,
    validateFieldOnChange,
    validateAll,
    isFormValid,
    clearFieldError,
    clearAllErrors
  };
};
